import { ReactNode, createContext, useContext, useState } from "react";
import { enUS as dateEn, es as dateEs } from "date-fns/esm/locale";

import { Locale as DateLocale } from "date-fns";
import { Dictionary } from "../locale/dictionary";
import { LengthUnits } from "convert-units/definitions/length";
import { SpeedUnits } from "convert-units/definitions/speed";
import { Units } from "../convert";
import { VolumeFlowRateUnits } from "convert-units/definitions/volumeFlowRate";
import { decimalPlaces, formatUnit } from "../unitFormat";
import config from "../config";
import en from "../locale/en";
import es from "../locale/es";

export type Lang = "en" | "es";

export interface UnitValue {
  value: number;
  unit: Units;
}

interface Locale {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  dict: Dictionary;
  dateLocale: DateLocale;
  unitSystem: "metric" | "imperial";
  units: {
    precip: {
      total: LengthUnits;
      intensity: SpeedUnits;
    };
    stream: {
      stage: LengthUnits;
      discharge: VolumeFlowRateUnits;
    };
  };
}

export const LocaleContext = createContext<Locale | undefined>(undefined);
export const useLocaleContext = () => {
  const localeContext = useContext(LocaleContext);
  if (!localeContext)
    throw new Error(
      "No LocaleContext.Provider found when calling useLocaleContext."
    );
  return localeContext;
};

export const LocaleContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [lang, setLang] = useState<Lang>(config.defaultLocale);
  const dict = lang === "en" ? en : es;
  const dateLocale = lang === "en" ? dateEn : dateEs;
  const unitSystem = lang === "en" ? "imperial" : "metric";
  const units: Locale["units"] = {
    precip: {
      total: lang === "en" ? "in" : "mm",
      intensity: lang === "en" ? "in/h" : "mm/h",
    },
    stream: {
      stage: lang === "en" ? "ft" : "m",
      discharge: lang === "en" ? "ft3/s" : "m3/s",
    },
  };

  const toggleLang = setLang.bind(null, lang === "en" ? "es" : "en");

  return (
    <LocaleContext.Provider
      value={{ lang, setLang, toggleLang, dict, dateLocale, unitSystem, units }}
    >
      {children}
    </LocaleContext.Provider>
  );
};

// Re-exported for backward compatibility — callers that already import from
// LocaleContext do not need to change their import path.
export { decimalPlaces, formatUnit };

export const unitFor = (
  units: Locale["units"],
  measure: "total" | "intensity" | "stage" | "discharge"
): Units => {
  switch (measure) {
    case "total":
      return units.precip.total;
    case "intensity":
      return units.precip.intensity;
    case "stage":
      return units.stream.stage;
    case "discharge":
      return units.stream.discharge;
  }
};
